import React from "react";
import { theme } from "antd";
import type { GlobalToken } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { radius } from "@libertify/ui";

interface DashedAddButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  loading?: boolean;
  /** Dashed border around the icon's own little box — on for the rail's
   *  "Add a new add-on", off for the Documents card's "Add a document". The
   *  outer button keeps its dashed border either way. */
  iconBordered?: boolean;
  /** Left-align (icon + label, rail default) vs center (Documents). */
  centered?: boolean;
}

/**
 * Full-width dashed "add" affordance: a small dashed box around the icon,
 * label beside it. Originally the rail's "Add a new add-on" button
 * (`AddonRail`) — pulled out here so the Documents card's "Add a document"
 * button renders from the exact same styled components instead of a
 * lookalike, and the two can't visually drift apart.
 */
export const DashedAddButton: React.FC<DashedAddButtonProps> = ({
  icon,
  label,
  onClick,
  loading,
  iconBordered = true,
  centered = false,
}) => {
  const { token } = theme.useToken();

  return (
    <AddButton
      $token={token}
      $centered={centered}
      type="button"
      onClick={onClick}
      disabled={loading}
    >
      <PlusBox $token={token} $bordered={iconBordered}>
        {loading ? <LoadingOutlined spin /> : icon}
      </PlusBox>
      {label}
    </AddButton>
  );
};

// ─── Styled components ───

interface TokenProps {
  $token: GlobalToken;
}

// Small dashed square around the icon. Neutral at rest; turns green on
// button hover (see AddButton below) — unless `$bordered` is off, in which
// case it never draws a border at all (Documents' "Add a document").
const PlusBox = styled.span<TokenProps & { $bordered: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: ${({ $bordered, $token }) =>
    $bordered ? `1px dashed ${$token.colorBorder}` : "none"};
  border-radius: 8px;
  color: ${({ $token }) => $token.colorPrimaryActive};
  transition: all ${({ $token }) => $token.motionDurationFast};
`;

const AddButton = styled.button<TokenProps & { $centered: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $centered }) => ($centered ? "center" : "flex-start")};
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border-radius: ${radius.box}px;
  cursor: pointer;
  background: transparent;
  border: 1px dashed ${({ $token }) => $token.colorBorder};
  color: ${({ $token }) => $token.colorPrimaryActive};
  font-family: inherit;
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 600;
  transition: all ${({ $token }) => $token.motionDurationFast};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:hover:not(:disabled) {
    border-color: ${({ $token }) => $token.colorPrimaryActive};
    background: ${({ $token }) => $token.colorPrimaryBg};
  }

  &:hover:not(:disabled) ${PlusBox} {
    border-color: ${({ $token }) => $token.colorPrimaryActive};
  }
`;
